// components/collaborationPanel/CollaborationPanel.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import "./collaborationPanel.css";
import { toast } from "react-toastify";

export default function CollaborationPanel({
  draft,
  onAddComment,
  onDeleteComment,
  onSaveWriterNotes,
  onSaveEditorNotes,
  currentUser,
  canPost = false,
  canEditWriterNotes = false,
  canEditEditorNotes = false,
  mentionUsers = [],
}) {

  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const commentInputRef = useRef(null);

  const [writerNotes, setWriterNotes] = useState(draft.writerNotes || "");
  const [editorNotes, setEditorNotes] = useState(draft.editorNotes || "");
  const [lastSavedWriter, setLastSavedWriter] = useState(draft.writerNotes || "");
  const [lastSavedEditor, setLastSavedEditor] = useState(draft.editorNotes || "");

  const [savingWriter, setSavingWriter] = useState(false);
  const [savingEditor, setSavingEditor] = useState(false);

  useEffect(() => {
    setWriterNotes(draft.writerNotes || "");
    setEditorNotes(draft.editorNotes || "");
    setLastSavedWriter(draft.writerNotes || "");
    setLastSavedEditor(draft.editorNotes || "");
    setNewComment("");
  }, [draft?._id, draft?.writerNotes, draft?.editorNotes]);

  useEffect(() => {
    return () => {
      if (writerTimer.current) clearTimeout(writerTimer.current);
      if (editorTimer.current) clearTimeout(editorTimer.current);
    };
  }, []);

  const writerTimer = useRef(null);
  const editorTimer = useRef(null);

  const writerReqSeq = useRef(0);
  const editorReqSeq = useRef(0);

  const debouncedSaveWriter = (val) => {
    setWriterNotes(val);
    if (!canPost || !onSaveWriterNotes || !canEditWriterNotes || !draft?._id) return;
    if (writerTimer.current) clearTimeout(writerTimer.current);
    writerTimer.current = setTimeout(async () => {
      setSavingWriter(true);
      const watchdog = setTimeout(() => {
        // safety switch: clear stuck UI after 10s
        setSavingWriter(false);
        toast.error("Saving writer notes is taking too long. Please check your connection.");
      }, 10000);

      const mySeq = ++writerReqSeq.current;
      try {
        await onSaveWriterNotes(draft?._id, val);
        if (mySeq === writerReqSeq.current) setLastSavedWriter(val);
        setLastSavedWriter(val);
      } catch (err) {
        setWriterNotes(lastSavedWriter);
        const msg = err?.response?.data?.message || "Could not save writer notes.";
        toast.error(msg);
      } finally {
        clearTimeout(watchdog);
        setSavingWriter(false);
      }
    }, 600);
  };

  const debouncedSaveEditor = (val) => {
    setEditorNotes(val);
    if (!canPost || !onSaveEditorNotes || !canEditEditorNotes || !draft?._id) return;
    if (editorTimer.current) clearTimeout(editorTimer.current);
    editorTimer.current = setTimeout(async () => {
      setSavingEditor(true);
      const watchdog = setTimeout(() => {
        // safety switch: clear stuck UI after 10s
        setSavingEditor(false);
        toast.error("Saving editor notes is taking too long. Please check your connection.");
      }, 10000);

      const mySeq = ++editorReqSeq.current;
      try {
        await onSaveEditorNotes(draft?._id, val);
        if (mySeq === editorReqSeq.current) setLastSavedEditor(val);
        setLastSavedEditor(val);
      } catch (err) {
        setEditorNotes(lastSavedEditor);
        const msg = err?.response?.data?.message || "Could not save editor notes.";
        toast.error(msg);
      } finally {
        clearTimeout(watchdog);
        setSavingEditor(false);
      }
    }, 600);
  };

  const roleMap = {
    1: "Reader",
    2: "Writer",
    3: "Editor",
    4: "Lead Editor",
    5: "Admin",
  };

  const dirty = newComment.trim().length > 0;

  const filteredMentionUsers = useMemo(() => {
    if (!showMentionMenu) return [];
    if (!mentionQuery) return mentionUsers;
    const q = mentionQuery.toLowerCase();
    return mentionUsers.filter(
      (u) =>
        u.username.toLowerCase().startsWith(q) ||
        (u.displayName || "").toLowerCase().includes(q)
    );
  }, [mentionQuery, mentionUsers, showMentionMenu]);

  const handleSelectMention = (u) => {
    setNewComment((prev) => {
      const textarea = commentInputRef.current;
      const cursorPos = textarea ? textarea.selectionStart : prev.length;
      const before = prev.slice(0, cursorPos);
      const after = prev.slice(cursorPos);

      // Replace "@partial" with "@fullUsername"
      const replacedBefore = before.replace(
        /@([A-Za-z0-9_]*)$/,
        `@${u.username}`
      );

      const nextText = replacedBefore + after;

      // Restore caret just after the mention
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
          const newPos = replacedBefore.length;
          textarea.setSelectionRange(newPos, newPos);
        }
      });

      return nextText;
    });

    setMentionQuery("");
    setShowMentionMenu(false);
  };

  const renderCommentText = (text) => {
    const raw = text || "";
    const parts = raw.split(/(@[A-Za-z0-9_]+)/g);

    return parts.map((part, idx) => {
      if (/^@[A-Za-z0-9_]+$/.test(part)) {
        return (
          <span key={idx} className="comment-mention">
            {part}
          </span>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const handleAdd = async () => {
    const text = newComment.trim();
    if (!text || !canPost) return;

    setSubmitting(true);
    try {
      await onAddComment?.(draft._id, text);
      setNewComment("");
      setMentionQuery("");
      setShowMentionMenu(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewComment("");
    setMentionQuery("");
    setShowMentionMenu(false);
  };

  const noteStatus = ({ val, last, saving, canEdit, canPost, hasId }) => {
    if (!hasId || !canPost) return "";          // you already show a placeholder in <textarea>
    if (!canEdit) return "Read-only";
    if (saving) return "Saving…";
    return (val ?? "") === (last ?? "") ? "Saved" : "Unsaved";
  };

  const writerStatus = noteStatus({
    val: writerNotes,
    last: lastSavedWriter,
    saving: savingWriter,
    canEdit: canEditWriterNotes,
    canPost,
    hasId: !!draft?._id,
  });

  const editorStatus = noteStatus({
    val: editorNotes,
    last: lastSavedEditor,
    saving: savingEditor,
    canEdit: canEditEditorNotes,
    canPost,
    hasId: !!draft?._id,
  });

  return (
    <div className="dc-body">
      <div className="dc-notes-pairing-grid">
        <div className="dc-notes-pairing">
          <div className="dc-section-title">
            Writer's Notes
          </div>
          <div className="dc-hint">
            A space for writers to capture background notes that don’t belong in the article itself. Creates transparency and helps the editor understand intent and reduces confusion about unfinished sections.
          </div>
          <textarea
            className="dc-notes"
            placeholder={
              !canPost
                ? "Save draft to take notes"
                : canEditWriterNotes
                  ? "Share notes with your editor..."
                  : "Only the assigned writer can edit these notes."
            }
            value={writerNotes}
            onChange={(e) => debouncedSaveWriter(e.target.value)}
            onBlur={() => debouncedSaveWriter(writerNotes)}
            disabled={!canPost}
            readOnly={!canEditWriterNotes}
            aria-readonly={!canEditWriterNotes}
            rows={5}
          />
          <footer className="dc-footer compact">
            <span className="dc-saving">{writerStatus}</span>
          </footer>
        </div>

        <div className="dc-notes-pairing">
          <div className="dc-section-title">
            Editor's Notes
          </div>
          <div className="dc-hint">
            A space for editors to share macro level feedback, key decisions, and next steps. Allows for broader structural feedback that is not easily shared in inline comments.
          </div>
          <textarea
            className="dc-notes"
            placeholder={
              !canPost
                ? "Save draft to take notes"
                : canEditEditorNotes
                  ? "Share notes with your writer..."
                  : "Only the assigned editor can edit these notes."
            }
            value={editorNotes}
            onChange={(e) => debouncedSaveEditor(e.target.value)}
            onBlur={() => debouncedSaveEditor(editorNotes)}
            disabled={!canPost}
            readOnly={!canEditEditorNotes}
            aria-readonly={!canEditEditorNotes}
            rows={5}
          />
          <footer className="dc-footer compact">
            <span className="dc-saving">{editorStatus}</span>
          </footer>
        </div>
      </div>

      <div className="dc-section">
        <div className="dc-section-title">
          Comments
        </div>
        <div className="dc-hint">
          Quick discussions about specific parts of the text. Ask questions, suggest edits, or clarify details.
        </div>
        <textarea
          className="dc-notes"
          placeholder={canPost ? "Share a new comment..." : "Save draft to comment"}
          value={newComment}
          ref={commentInputRef}
          onChange={(e) => {
            const value = e.target.value;
            setNewComment(value);

            const cursorPos = e.target.selectionStart ?? value.length;
            const textBeforeCursor = value.slice(0, cursorPos);
            const match = textBeforeCursor.match(/@([A-Za-z0-9_]*)$/);

            if (match) {
              setMentionQuery(match[1]); // part after @
              setShowMentionMenu(true);
            } else {
              setMentionQuery("");
              setShowMentionMenu(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowMentionMenu(false);
              setMentionQuery("");
            }
          }}
          rows={5}
          disabled={!canPost}
        />
      </div>

      {showMentionMenu && filteredMentionUsers.length > 0 && (
        <div className="mention-menu">
          {filteredMentionUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              className="mention-menu-item"
              onClick={() => handleSelectMention(u)}
            >
              @{u.username}{" "}
              <span className="mention-display-name">
                {u.displayName}
              </span>
            </button>
          ))}
        </div>
      )}

      <footer className="dc-footer">
        <button
          className="dc-btn ghost"
          onClick={handleCancel}
          disabled={!dirty}
        >
          Cancel
        </button>

        <button
          className="dc-btn primary"
          onClick={handleAdd}
          disabled={!canPost || !dirty || submitting}
          aria-disabled={!canPost || !dirty || submitting}

        >
          {submitting ? "Posting..." : "Add Comment"}
        </button>
      </footer>

      <div className="dc-comments">
        {[...(draft.comments || [])]
          .sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))
          .map((c) => {
            const when = c.timestamp || c.createdAt;
            const canDelete = currentUser?.role >= 4 || String(c.authorId) === String(currentUser?.id);
            return (
              <div
                key={c._id}
                id={`comment-${c._id}`}
                className="dc-comment"
              >
                <div className="dc-comment-header">
                  <span className="dc-comment-author">{c.authorName || "Unknown User"}</span>
                  {c.role && <span className="dc-comment-role">({roleMap[c.role] || "Unknown Role"}){" "}</span>}
                  {when && (
                    <span className="dc-comment-time">
                      {new Date(when).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                  {canDelete && (
                    <button
                      className="dc-comment-delete"
                      title="Delete comment"
                      onClick={() => onDeleteComment?.(draft._id, c._id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="dc-comment-body">
                  {renderCommentText(c.text)}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
